import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import { TicketProgressBar } from 'features/libra/ticketMenu/TicketProgressBar'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

type TicketDetailViewProps = {
  tickets: ReadonlyArray<LibraTicket>
}

export const TicketDetailView: FC<TicketDetailViewProps> = ({ tickets }) => {
  // Get workflow IDs from tickets
  const workflowIds = useMemo(() => {
    return Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      )
    )
  }, [tickets])

  const { libraWorkflows } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  const handleTicketClick = useCallback((ticket: LibraTicket) => {
    const orgId = ticket.tags.organization as string | undefined
    const workflowId = ticket.workflowId
    if (!orgId || !workflowId) return

    const url = `/libra/${orgId}/${workflowId}?ticket=${ticket.id}`
    window.location.href = url
  }, [])

  if (tickets.length === 0) {
    return (
      <Container>
        <EmptyState>Select an asset to view tickets</EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Title>Tickets</Title>
        <Count>{tickets.length}</Count>
      </Header>
      <TicketList>
        {tickets.map((ticket) => {
          const workflow = workflowMap.get(ticket.workflowId)
          return (
            <TicketCard
              key={ticket.id}
              onClick={() => handleTicketClick(ticket)}>
              <TicketHeader>
                <TicketContent>
                  <TicketTitle>{ticket.title}</TicketTitle>
                  {workflow && <WorkflowName>{workflow.name}</WorkflowName>}
                </TicketContent>
                {workflow && (
                  <ProgressContainer>
                    <TicketProgressBar
                      ticket={ticket}
                      workflow={workflow}
                      circle
                    />
                  </ProgressContainer>
                )}
              </TicketHeader>
            </TicketCard>
          )
        })}
      </TicketList>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 8px;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const Count = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
`

const TicketList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
`

const TicketCard = styled.div`
  padding: 0.875rem;
  margin-bottom: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray[700]};
    border-color: ${({ theme }) => theme.forecast.primary};
    transform: translateY(-1px);
  }
`

const TicketHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const TicketContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TicketTitle = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WorkflowName = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProgressContainer = styled.div`
  flex-shrink: 0;
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`
